import * as React from 'react'
import { Flex, Input, InputNumber, Select, Tooltip } from 'antd'
import { Button } from '@pushly/aqe/lib/components'
import {
    CaretUpFilled,
    CaretDownFilled,
    DeleteOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons'
import { ItemIdStrategy } from '@pushly/models/lib/enums/catalogs/item-id-strategy'
import { DISPLAY_STRATEGY_JSONPATH_CUSTOM, ILocalItemIdConfig, getDisplayStrategy } from './types'

interface IProps {
    configs: ILocalItemIdConfig[]
    disabled: boolean
    onAdd: () => void
    onMove: (urlIndex: number, direction: 'up' | 'down') => void
    onDelete: (urlIndex: number) => void
    onChangeStrategy: (urlIndex: number, displayStrategy: string) => void
    onChangeField: (urlIndex: number, field: keyof ILocalItemIdConfig, value: string | number | undefined) => void
}

/**
 * Renders the ordered list of "Item ID Resolution" strategies for one catalog
 * config. Each row holds an order-handle, a strategy <Select>, the
 * strategy-specific fields, and a delete button. Strategies are tried in order
 * top-to-bottom — first non-empty result wins.
 */
function ItemIdStrategies(props: IProps) {
    const { configs, disabled } = props
    const canDelete = configs.length > 1

    return (
        <Flex vertical gap={8}>
            {configs.map((config, idx) => (
                <StrategyRow
                    key={idx}
                    config={config}
                    index={idx}
                    isFirst={idx === 0}
                    isLast={idx === configs.length - 1}
                    disabled={disabled}
                    canDelete={canDelete}
                    onMove={(dir) => props.onMove(idx, dir)}
                    onDelete={() => props.onDelete(idx)}
                    onChangeStrategy={(s) => props.onChangeStrategy(idx, s)}
                    onChangeField={(f, v) => props.onChangeField(idx, f, v)}
                />
            ))}

            <Flex justify="flex-start">
                <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={props.onAdd}>
                    Add Strategy
                </Button>
            </Flex>
        </Flex>
    )
}

interface IRowProps {
    config: ILocalItemIdConfig
    index: number
    isFirst: boolean
    isLast: boolean
    disabled: boolean
    canDelete: boolean
    onMove: (direction: 'up' | 'down') => void
    onDelete: () => void
    onChangeStrategy: (displayStrategy: string) => void
    onChangeField: (field: keyof ILocalItemIdConfig, value: string | number | undefined) => void
}

function StrategyRow(props: IRowProps) {
    const { config, index, isFirst, isLast, disabled } = props
    const displayStrategy = getDisplayStrategy(config)

    return (
        <div className="item-id-strategy-row">
            <Flex gap={8} align="center" className="item-id-strategy-row-head">
                <OrderHandle
                    index={index}
                    canMoveUp={!isFirst && !disabled}
                    canMoveDown={!isLast && !disabled}
                    onMove={props.onMove}
                />

                <Select
                    value={displayStrategy}
                    onChange={props.onChangeStrategy}
                    disabled={disabled}
                    style={{ width: 220 }}
                >
                    <Select.OptGroup label="URL Source">
                        <Select.Option value={ItemIdStrategy.FULL_URL}>Full URL</Select.Option>
                        <Select.Option value={ItemIdStrategy.PATH_SEGMENT}>Segment Path</Select.Option>
                        <Select.Option value={ItemIdStrategy.AFTER_SEGMENT}>After Segment</Select.Option>
                    </Select.OptGroup>
                    <Select.OptGroup label="Page Source">
                        <Select.Option value={DISPLAY_STRATEGY_JSONPATH_CUSTOM}>JSONPATH (Custom)</Select.Option>
                        <Select.Option value={ItemIdStrategy.CONTENT_JSON_PATH}>NEXT DATA</Select.Option>
                        <Select.Option value={ItemIdStrategy.LD_JSON_IDENTIFIER}>
                            LD-JSON Thing.Identifier
                        </Select.Option>
                    </Select.OptGroup>
                </Select>

                <div className="item-id-strategy-row-summary">
                    <StrategySummary config={config} />
                </div>

                <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={disabled || !props.canDelete}
                    onClick={props.onDelete}
                />
            </Flex>

            <StrategyFields
                config={config}
                displayStrategy={displayStrategy}
                disabled={disabled}
                onChangeField={props.onChangeField}
            />
        </div>
    )
}

function OrderHandle(props: {
    index: number
    canMoveUp: boolean
    canMoveDown: boolean
    onMove: (direction: 'up' | 'down') => void
}) {
    return (
        <Flex align="center" gap={6} className="item-id-strategy-row-order">
            <span className="item-id-strategy-row-index">{props.index + 1}</span>
            <Flex vertical gap={2}>
                <Tooltip title="Move up">
                    <Button
                        type="default"
                        size="small"
                        icon={<CaretUpFilled />}
                        disabled={!props.canMoveUp}
                        onClick={() => props.onMove('up')}
                    />
                </Tooltip>
                <Tooltip title="Move down">
                    <Button
                        type="default"
                        size="small"
                        icon={<CaretDownFilled />}
                        disabled={!props.canMoveDown}
                        onClick={() => props.onMove('down')}
                    />
                </Tooltip>
            </Flex>
        </Flex>
    )
}

/** Short inline summary next to the Select for at-a-glance recognition. */
function StrategySummary(props: { config: ILocalItemIdConfig }) {
    const { config } = props
    if (config.strategy === ItemIdStrategy.FULL_URL) {
        return <span className="muted">Uses the full page URL as the item ID</span>
    }
    if (config.strategy === ItemIdStrategy.LD_JSON_IDENTIFIER) {
        return (
            <span className="muted">
                Reads the <code>identifier</code> field from the matched JSON-LD object.
            </span>
        )
    }
    return null
}

function StrategyFields(props: {
    config: ILocalItemIdConfig
    displayStrategy: string
    disabled: boolean
    onChangeField: (field: keyof ILocalItemIdConfig, value: string | number | undefined) => void
}) {
    const { config, displayStrategy, disabled, onChangeField } = props

    if (config.strategy === ItemIdStrategy.PATH_SEGMENT) {
        return (
            <Field
                label="Segment Index"
                tip="Zero-based index of the URL path segment to use as the item ID."
            >
                <InputNumber
                    value={config.segment_index ?? undefined}
                    onChange={(v) => onChangeField('segment_index', v ?? undefined)}
                    disabled={disabled}
                    placeholder="e.g. 2"
                    min={0}
                    precision={0}
                    controls={true}
                    className="segment-index-input"
                    style={{ width: 100 }}
                />
            </Field>
        )
    }

    if (config.strategy === ItemIdStrategy.AFTER_SEGMENT) {
        return (
            <Field
                label="Resource String"
                tip="The path segment that identifies the resource type. The item ID is the segment immediately following."
            >
                <Input
                    value={config.after_segment ?? undefined}
                    onChange={(e) => onChangeField('after_segment', e.target.value || undefined)}
                    disabled={disabled}
                    placeholder="e.g. recipes"
                    autoComplete="off"
                />
            </Field>
        )
    }

    if (displayStrategy === DISPLAY_STRATEGY_JSONPATH_CUSTOM) {
        return (
            <>
                <Field
                    label="Element ID"
                    tip="The id attribute of the <script> tag containing the JSON to parse. Defaults to __NEXT_DATA__."
                >
                    <Input
                        value={config.element_id ?? ''}
                        onChange={(e) => onChangeField('element_id', e.target.value || undefined)}
                        disabled={disabled}
                        placeholder="__NEXT_DATA__"
                        autoComplete="off"
                    />
                </Field>
                <Field
                    label="JSONPath"
                    tip="JSONPath expression to evaluate against the parsed JSON. Example: $.props.pageProps.data.content[0].id"
                >
                    <Input
                        value={config.json_path ?? ''}
                        onChange={(e) => onChangeField('json_path', e.target.value || undefined)}
                        disabled={disabled}
                        placeholder="$.props.pageProps.data.id"
                        autoComplete="off"
                    />
                </Field>
                <Field
                    label="Regex Pattern"
                    tip="Optional regex with one capture group, applied to the JSONPath result. Leave empty to use the JSONPath result as-is."
                >
                    <Input
                        value={config.regex_pattern ?? ''}
                        onChange={(e) => onChangeField('regex_pattern', e.target.value || undefined)}
                        disabled={disabled}
                        placeholder="Optional regex with one capture group"
                        autoComplete="off"
                    />
                </Field>
            </>
        )
    }

    if (displayStrategy === ItemIdStrategy.CONTENT_JSON_PATH) {
        return (
            <>
                <Field
                    label="JSONPath"
                    tip="JSONPath expression to evaluate against the parsed __NEXT_DATA__ JSON. Example: $.props.pageProps.data.content[0].id"
                >
                    <Input
                        value={config.json_path ?? ''}
                        onChange={(e) => onChangeField('json_path', e.target.value || undefined)}
                        disabled={disabled}
                        placeholder="$.props.pageProps.data.id"
                        autoComplete="off"
                    />
                </Field>
                <Field
                    label="Regex Pattern"
                    tip="Optional regex with one capture group, applied to the JSONPath result."
                >
                    <Input
                        value={config.regex_pattern ?? ''}
                        onChange={(e) => onChangeField('regex_pattern', e.target.value || undefined)}
                        disabled={disabled}
                        placeholder="Optional regex with one capture group"
                        autoComplete="off"
                    />
                </Field>
            </>
        )
    }

    return null
}

/** Inline label + control row, mirroring Form.Item's horizontal layout. */
function Field(props: { label: string; tip: string; children: React.ReactNode }) {
    return (
        <Flex gap={8} align="center" className="item-id-strategy-row-field">
            <span className="item-id-strategy-row-label">
                {props.label}{' '}
                <Tooltip title={props.tip}>
                    <QuestionCircleOutlined className="muted" />
                </Tooltip>
            </span>
            <div className="item-id-strategy-row-field-control">{props.children}</div>
        </Flex>
    )
}

export default ItemIdStrategies
